import { API_BASE } from "@/lib/api";
import { Copy, Terminal, Zap, Puzzle } from "lucide-react";
import { toast } from "sonner";

export default function Integrations() {
  const base = API_BASE;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const snippets = [
    {
      title: "curl · single log",
      lang: "bash",
      code: `curl -X POST "${base}/v1/ingest" \\
  -H "X-API-Key: fq_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "type": "log",
    "log": {
      "source": "syslog",
      "host": "srv-db-01",
      "message": "5 failed root SSH attempts from 185.220.101.45",
      "severity": "critical"
    }
  }'`,
    },
    {
      title: "curl · IOC → auto investigation",
      lang: "bash",
      code: `curl -X POST "${base}/v1/ingest" \\
  -H "X-API-Key: fq_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "type": "ioc",
    "customer": {
      "customer_name": "Acme Bank",
      "industry": "Financial Services",
      "contact_email": "ciso@acme.com",
      "severity": "critical",
      "affected_assets": ["srv-web-02"]
    },
    "iocs": [
      {"kind": "ip",     "value": "185.220.101.45"},
      {"kind": "domain", "value": "phish.example.com"}
    ],
    "auto_run": true
  }'`,
    },
    {
      title: "Python · batch logs",
      lang: "python",
      code: `import requests
r = requests.post(
    "${base}/v1/ingest",
    headers={"X-API-Key": "fq_live_YOUR_KEY"},
    json={
        "type": "logs_batch",
        "logs": [
            {"source": "elk", "host": "web-01",
             "severity": "error", "message": "SQL injection blocked by WAF"},
            {"source": "elk", "host": "web-02",
             "severity": "warning", "message": "Rate limit approaching"},
        ],
    },
    timeout=30,
)
print(r.json())`,
    },
    {
      title: "Splunk HEC output (props.conf)",
      lang: "ini",
      code: `[forensiq_ingest]
DEST_KEY = _MetaData:Index
FORMAT = forensiq
# Then in outputs.conf on your HF/UF:
# [httpout:forensiq]
# url = ${base}/v1/ingest
# httpAuthHeader = X-API-Key: fq_live_YOUR_KEY
# payloadTemplate = {"type":"log","log":{"source":"splunk","host":"$host$","message":"$_raw$","severity":"$severity$"}}`,
    },
    {
      title: "Elastic Filebeat http_endpoint output",
      lang: "yaml",
      code: `output.http:
  hosts: ["${base}/v1/ingest"]
  headers:
    X-API-Key: "fq_live_YOUR_KEY"
    Content-Type: "application/json"
  wrap:
    template: |
      {"type":"log","log":{"source":"filebeat","host":"{{.host.name}}","severity":"{{.log.level}}","message":"{{.message}}"}}`,
    },
    {
      title: "n8n / Zapier · generic HTTP node",
      lang: "text",
      code: `Method:  POST
URL:     ${base}/v1/ingest
Header:  X-API-Key: fq_live_YOUR_KEY
Header:  Content-Type: application/json
Body:    {"type":"ioc","customer":{...},"iocs":[...]}`,
    },
  ];

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <header className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
          <Puzzle className="w-3.5 h-3.5" /> Wire any system to ForensiQ
        </div>
        <h1 className="font-display font-black text-5xl mt-2">Integrations</h1>
        <p className="text-[#8E9196] mt-1">One universal endpoint. Every SIEM, EDR, log shipper, ticketing tool, or bespoke agent can talk to ForensiQ over plain HTTPS with a scoped API key.</p>
      </header>

      <div className="border border-[#00F0FF]/40 bg-[#00F0FF]/[0.04] rounded-sm p-5 mb-6" data-testid="endpoint-card">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF]">Universal ingest endpoint</div>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <span className="font-mono text-lg text-white">POST</span>
          <code className="font-mono text-lg text-[#00F0FF] break-all">{base}/v1/ingest</code>
          <button onClick={() => copy(`${base}/v1/ingest`)} className="p-2 text-[#8E9196] hover:text-white"><Copy className="w-4 h-4" /></button>
        </div>
        <div className="mt-3 text-xs text-[#8E9196] font-mono">
          Header: <span className="text-white">X-API-Key: fq_live_...</span> · Payload types: <span className="text-white">log · logs_batch · ioc · incident</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="snippet-grid">
        {snippets.map((s) => (
          <div key={s.title} className="border border-white/10 bg-[#121212] rounded-sm">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">
                <Terminal className="w-3.5 h-3.5" /> {s.title}
              </div>
              <button onClick={() => copy(s.code)} className="p-1.5 text-[#8E9196] hover:text-[#00F0FF]" data-testid={`copy-snippet-${s.title.replace(/\s/g, '-')}`}>
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
            <pre className="p-4 font-mono text-[11px] leading-relaxed text-white/90 overflow-x-auto whitespace-pre">{s.code}</pre>
          </div>
        ))}
      </div>

      <div className="mt-8 border border-white/10 bg-[#121212] rounded-sm p-5">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
          <Zap className="w-3.5 h-3.5" /> What happens next
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3 text-sm">
          <div>
            <div className="font-display text-lg">1 · Ingest</div>
            <p className="text-[#8E9196]">Log/IOC lands in the pipeline with rate-limit + last-used tracking on the API key.</p>
          </div>
          <div>
            <div className="font-display text-lg">2 · Automate</div>
            <p className="text-[#8E9196]">Automation rules evaluate the event and can auto-create incidents or investigations.</p>
          </div>
          <div>
            <div className="font-display text-lg">3 · Investigate</div>
            <p className="text-[#8E9196]">For IOCs, ForensiQ enriches across 6 sources + generates the AI narrative + downloadable report.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
